import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { X, Leaf, Check, Sparkles, GraduationCap } from "lucide-react";

const STORAGE_KEY = "rootspace_pricing_shown";
const STORAGE_EDUCATOR_KEY = "rootspace_is_educator";

const PricingPopup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const [isEducator, setIsEducator] = useState(false);

  useEffect(() => {
    // Check if user is logged in and this is their first time
    if (user) {
      const hasSeenPricing = localStorage.getItem(STORAGE_KEY);
      const storedIsEducator = localStorage.getItem(STORAGE_EDUCATOR_KEY);
      
      // If user has educator flag or previously marked as educator, skip
      if (user.is_educator || storedIsEducator === "true") {
        setIsEducator(true);
        return;
      }
      
      // Check if this is a first-time user on free tier who hasn't seen pricing
      if (!hasSeenPricing && user.subscription_tier === "free") {
        // Small delay so the page loads first
        const timer = setTimeout(() => {
          setShowPopup(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setShowPopup(false);
  };

  const handleViewPricing = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setShowPopup(false);
    navigate("/pricing");
  };

  const handleEducator = () => {
    localStorage.setItem(STORAGE_EDUCATOR_KEY, "true");
    setShowPopup(false);
  };

  const handleMaybeLater = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setShowPopup(false);
  };

  if (!showPopup) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative w-full max-w-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="overflow-hidden border-0 shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 via-emerald-500 to-teal-500 p-6 text-white relative">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20 transition-colors"
                data-testid="pricing-popup-close"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-full bg-white/20">
                  <Leaf className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold">Welcome to RootSpace!</h2>
              </div>
              <p className="text-white/90">
                Choose the plan that fits your gardening journey
              </p>
            </div>

            <CardContent className="p-6">
              {/* Quick Plan Overview */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {/* Free Plan */}
                <div className="p-4 rounded-xl border-2 border-gray-200 bg-gray-50">
                  <h3 className="font-bold text-lg mb-1">The Planner</h3>
                  <p className="text-2xl font-bold text-green-600 mb-2">Free</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> 1 garden plan
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Plant database
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Design studio
                    </li>
                  </ul>
                </div>

                {/* Standard Plan */}
                <div className="p-4 rounded-xl border-2 border-green-500 bg-green-50 relative">
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500">
                    Popular
                  </Badge>
                  <h3 className="font-bold text-lg mb-1">The Gardener</h3>
                  <p className="text-2xl font-bold text-green-600 mb-2">
                    $7<span className="text-sm font-normal">/mo</span>
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Unlimited plans
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Plant identifier
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" /> Weather alerts
                    </li>
                  </ul>
                </div>

                {/* Premium Plan */}
                <div className="p-4 rounded-xl border-2 border-purple-300 bg-purple-50">
                  <h3 className="font-bold text-lg mb-1">Master Gardener</h3>
                  <p className="text-2xl font-bold text-purple-600 mb-2">
                    $19<span className="text-sm font-normal">/mo</span>
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500" /> All features
                    </li>
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500" /> AI renders
                    </li>
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500" /> Pro support
                    </li>
                  </ul>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button 
                  onClick={handleViewPricing}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600"
                  data-testid="pricing-popup-view-plans"
                >
                  View Full Pricing Details
                </Button>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      localStorage.setItem(STORAGE_EDUCATOR_KEY, "true");
                      setShowPopup(false);
                      navigate("/educators");
                    }}
                    className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="pricing-popup-educator"
                  >
                    <GraduationCap className="w-4 h-4 mr-2" />
                    I&apos;m an Educator
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={handleMaybeLater}
                    className="flex-1"
                    data-testid="pricing-popup-later"
                  >
                    Maybe Later
                  </Button>
                </div>
              </div>

              <p className="text-center text-xs text-muted-foreground mt-4">
                Free users can always access the Design Studio, Plant Database, and GROWTH challenges
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PricingPopup;
